// passiveAssetService.js
// Thin wrappers around the IoT Beacon + RFID APIs documented in
// IOT_BEACON_RFID_API_TEST_GUIDE.md. These endpoints are raw-read oriented,
// so callers should normalize responses via passiveAssetNormalizers before
// rendering.
//
// NOTE on auth: although the API guide shows `Authorization: Bearer <token>`,
// the live AssetPro deployment expects the RAW token with NO "Bearer " prefix
// (a Bearer prefix returns 403 "Forbidden resource"). This matches the working
// API Tester request exactly:
//   accept: application/json
//   X-Tenant-Id: newa
//   X-Api-Version: 2.0
//   Authorization: <raw token>   (no Bearer)
//   (no Content-Type on GET)

import { getToken } from './authService';
import { getServer } from './serverConfig';

const TENANT_ID = 'newa';
const API_VERSION = '2.0';

// GET against the IoT endpoints, replicating the API Tester's working request.
// Returns { success, data } / { success:false, status, error }.
const iotGet = async (endpoint) => {
  const token = getToken();
  const url = `${getServer()}${endpoint}`;

  const headers = {
    accept: 'application/json',
    'X-Tenant-Id': TENANT_ID,
    'X-Api-Version': API_VERSION
  };
  if (token) {
    headers.Authorization = token; // raw token, NO "Bearer " prefix
  }

  const res = await fetch(url, { method: 'GET', headers });

  const text = await res.text();
  if (!res.ok) {
    return { success: false, status: res.status, error: text || `HTTP ${res.status}` };
  }
  try {
    return { success: true, data: text ? JSON.parse(text) : null };
  } catch (e) {
    return { success: true, data: text };
  }
};

const buildQuery = (params) => {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === null || value === undefined || value === '') return;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  });
  return parts.length ? `?${parts.join('&')}` : '';
};

const validateTimeRange = (from, to) => {
  if (from === null || from === undefined || to === null || to === undefined) {
    throw new Error('from and to (epoch milliseconds) are required');
  }
  if (Number(from) >= Number(to)) {
    throw new Error('from must be earlier than to');
  }
};

// GET /iot-device/beacon-data
// Requires from + to, and at least one of mac/imei. If both supplied, mac wins.
export function fetchBeaconData({ from, to, mac, imei }) {
  validateTimeRange(from, to);
  if (!mac && !imei) {
    throw new Error('Beacon MAC or Device IMEI is required to fetch beacon data');
  }

  const params = { from, to };
  if (mac) {
    params.mac = mac;
  } else if (imei) {
    params.imei = imei;
  }

  return iotGet(`/iot-device/beacon-data${buildQuery(params)}`);
}

// GET /iot-device/rfid-data
// Requires from + to, and at least one of rfid/instance_id or imei/device_id.
// Supports optional status filter.
export function fetchRfidData({ from, to, rfid, imei, status }) {
  validateTimeRange(from, to);
  if (!rfid && !imei) {
    throw new Error('RFID or Reader IMEI is required to fetch RFID data');
  }

  const params = { from, to };
  if (rfid) params.rfid = rfid;
  if (imei) params.imei = imei;
  if (status) params.status = status;

  return iotGet(`/iot-device/rfid-data${buildQuery(params)}`);
}

// GET /iot-device/rfid-data/status/:status/:from/:to
// Use this when only status is known (no rfid / imei filter).
export function fetchRfidByStatus({ status, from, to }) {
  validateTimeRange(from, to);
  if (!status) {
    throw new Error('status is required to fetch RFID data by status');
  }
  const s = encodeURIComponent(status);
  return iotGet(`/iot-device/rfid-data/status/${s}/${from}/${to}`);
}
